import { openDB } from 'idb';
import { DATA_VERSION } from './version';

// Databases that were opened before the store became per user. They hold a
// channel and team list built for whoever signed in first on this machine, so
// they are dropped the first time the new store is opened for that account.
const purgedLegacyDbs = new Set();

const purgeLegacyDb = accountId => {
  const legacyName = `cw-store-${accountId}`;
  if (purgedLegacyDbs.has(legacyName)) return;
  purgedLegacyDbs.add(legacyName);

  try {
    window.indexedDB.deleteDatabase(legacyName);
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (dbNames.includes(legacyName)) {
      localStorage.setItem(
        'cw-idb-names',
        JSON.stringify(dbNames.filter(name => name !== legacyName))
      );
    }
  } catch {
    // Storage is unavailable (private mode); nothing to clean up.
  }
};

export class DataManager {
  // `scopeId` identifies the signed in user. Everything cached here (inboxes,
  // teams, labels, canned responses) is filtered per user by the access rules,
  // so two people signing in on the same browser must not land in one store —
  // otherwise the second one keeps seeing the first one's channel list.
  constructor(accountId, scopeId = null) {
    this.modelsToSync = ['inbox', 'label', 'team', 'canned_response'];
    this.accountId = accountId;
    this.scopeId = scopeId;
    this.db = null;
  }

  get dbName() {
    return this.scopeId
      ? `cw-store-${this.accountId}-${this.scopeId}`
      : `cw-store-${this.accountId}`;
  }

  async initDb() {
    if (this.db) return this.db;
    if (this.scopeId) purgeLegacyDb(this.accountId);
    const { dbName } = this;
    this.db = await openDB(dbName, DATA_VERSION, {
      upgrade(db) {
        // Existing databases already carry the stores added in earlier versions,
        // and createObjectStore throws on a name that is already taken.
        const createStore = (name, options) => {
          if (db.objectStoreNames.contains(name)) return;
          db.createObjectStore(name, options);
        };

        createStore('cache-keys');
        createStore('inbox', { keyPath: 'id' });
        createStore('label', { keyPath: 'id' });
        createStore('team', { keyPath: 'id' });
        createStore('canned_response', { keyPath: 'id' });
      },
    });

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
